import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import ENV_CONFIG from '../config/environment-unified';
import useTranslation from '../hooks/useTranslation';

// Import G-Coin Icons
import GCoinIcon from '../img/icons/gcoin.png';
import GCoinBagIcon from '../img/icons/gcoin-bag.png';

// Styled Components
const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
  min-height: 100vh;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 40px;
`;

const Title = styled.h1`
  font-size: 36px;
  font-weight: bold;
  color: ${({ theme }) => theme.color.primaryText};
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
`;

const TitleIcon = styled.img`
  width: 48px;
  height: 48px;
  object-fit: contain;
`;

const Subtitle = styled.p`
  font-size: 18px;
  color: ${({ theme }) => theme.color.secondaryText};
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 40px;
`;

const StatCard = styled.div`
  background: ${({ theme }) => theme.color.cardBackground};
  border-radius: 16px;
  padding: 24px;
  text-align: center;
  border: 1px solid ${({ theme }) => theme.color.border};
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  }
`;

const StatIcon = styled.div`
  font-size: 32px;
  margin-bottom: 10px;
`;

const StatIconImg = styled.img`
  width: 40px;
  height: 40px;
  object-fit: contain;
  margin-bottom: 10px;
`;

const StatValue = styled.div`
  font-size: 28px;
  font-weight: bold;
  color: ${({ theme, type }) => {
    if (type === 'credit') return '#22c55e';
    if (type === 'debit') return '#ef4444';
    return theme.color.primaryText;
  }};
  margin-bottom: 5px;
`;

const StatLabel = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.color.secondaryText};
`;

const TransactionsContainer = styled.div`
  background: ${({ theme }) => theme.color.cardBackground};
  border-radius: 16px;
  border: 1px solid ${({ theme }) => theme.color.border};
  overflow: hidden;
`;

const TransactionsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid ${({ theme }) => theme.color.border};
`;

const TransactionsTitle = styled.h2`
  font-size: 20px;
  font-weight: bold;
  color: ${({ theme }) => theme.color.primaryText};
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FilterButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const FilterButton = styled.button`
  padding: 8px 16px;
  border-radius: 8px;
  border: 1px solid ${({ theme, active }) => active ? '#667eea' : theme.color.border};
  background: ${({ active }) => active ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'transparent'};
  color: ${({ theme, active }) => active ? '#fff' : theme.color.secondaryText};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #667eea;
    color: ${({ active }) => active ? '#fff' : '#667eea'};
  }
`;

const TransactionsList = styled.div`
  max-height: 600px;
  overflow-y: auto;
`;

const TransactionItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid ${({ theme }) => theme.color.border};
  transition: background 0.2s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${({ theme }) => theme.color.backgroundLighter};
  }
`;

const TransactionInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const TransactionIconWrapper = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ type }) => 
    type === 'credit' ? 'rgba(34, 197, 94, 0.15)' : 'rgba(239, 68, 68, 0.15)'};
  border: 2px solid ${({ type }) => 
    type === 'credit' ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)'};
`;

const TransactionIconImg = styled.img`
  width: 28px;
  height: 28px;
  object-fit: contain;
  filter: ${({ type }) => 
    type === 'credit' ? 'drop-shadow(0 0 4px rgba(34, 197, 94, 0.5))' : 'drop-shadow(0 0 4px rgba(239, 68, 68, 0.5))'};
`;

const TransactionDetails = styled.div``;

const TransactionDescription = styled.div`
  font-size: 15px;
  font-weight: 500;
  color: ${({ theme }) => theme.color.primaryText};
  margin-bottom: 4px;
`;

const TransactionDate = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.color.secondaryText};
`;

const TransactionAmount = styled.div`
  text-align: right;
`;

const Amount = styled.div`
  font-size: 18px;
  font-weight: bold;
  color: ${({ type }) => type === 'credit' ? '#22c55e' : '#ef4444'};
`;

const Balance = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.color.secondaryText};
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  
  div {
    width: 40px;
    height: 40px;
    border: 3px solid rgba(102, 126, 234, 0.2);
    border-top-color: #667eea;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${({ theme }) => theme.color.secondaryText};
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.5;
`;

const EmptyText = styled.p`
  font-size: 16px;
  margin-bottom: 20px;
`;

const BuyButton = styled(Link)`
  display: inline-block;
  padding: 12px 24px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 8px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(102, 126, 234, 0.4);
  }
`;

const ErrorMessage = styled.div`
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid #ef4444;
  color: #ef4444;
  padding: 20px;
  border-radius: 12px;
  text-align: center;
  margin: 20px 0;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: ${({ theme }) => theme.color.secondaryText};
  text-decoration: none;
  font-size: 14px;
  margin-bottom: 20px;
  transition: color 0.2s ease;

  &:hover {
    color: #667eea;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 14px;
  flex-wrap: wrap;
`;

const SecondaryButton = styled(Link)`
  display: inline-block;
  padding: 10px 18px;
  border-radius: 8px;
  font-weight: 600;
  text-decoration: none;
  border: 1px solid ${({ theme }) => theme.color.border};
  color: ${({ theme }) => theme.color.primaryText};
  background: transparent;
  transition: all 0.2s ease;

  &:hover {
    border-color: #667eea;
    color: #667eea;
    transform: translateY(-1px);
  }
`;

// Main Component
const GCoinTransactions = () => {
  const { t, currentLanguage } = useTranslation();
  const userData = useSelector((state) => state.user?.data);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // all, credit, debit
  const [stats, setStats] = useState({
    balance: 0,
    totalCredits: 0,
    totalDebits: 0,
    transactionCount: 0
  });

  useEffect(() => {
    if (userData?.id) {
      fetchTransactions();
      fetchBalance();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  const fetchBalance = async () => {
    try {
      const response = await axios.get(`${ENV_CONFIG.WALLET_BALANCE_ENDPOINT}/${userData.id}`, {
        timeout: 5000,
        withCredentials: true
      });

      if (response.data) {
        setStats(prev => ({
          ...prev,
          balance: response.data.balance || 0
        }));
      }
    } catch (err) {
      console.error('Error fetching balance:', err);
    }
  };

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${ENV_CONFIG.WALLET_TRANSACTIONS_ENDPOINT}/${userData.id}`, {
        timeout: 10000,
        withCredentials: true
      });

      if (response.data) {
        const txList = response.data.transactions || [];
        setTransactions(txList);
        
        // Calculate stats
        const totalCredits = txList
          .filter(tx => tx.type === 'credit')
          .reduce((sum, tx) => sum + parseFloat(tx.amount), 0);
        
        const totalDebits = txList
          .filter(tx => tx.type === 'debit')
          .reduce((sum, tx) => sum + parseFloat(tx.amount), 0);

        setStats(prev => ({
          ...prev,
          totalCredits,
          totalDebits,
          transactionCount: txList.length
        }));
      }
    } catch (err) {
      console.error('Error fetching transactions:', err);
      setError(t('transactions.errorLoading', 'Impossible de charger les transactions'));
      toast.error(t('transactions.errorLoading', 'Erreur lors du chargement des transactions'));
    } finally {
      setLoading(false);
    }
  };

  const getLocale = () => (currentLanguage === 'en' ? 'en-GB' : 'fr-FR');

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(getLocale(), {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatNumber = (num) => {
    return num.toLocaleString(getLocale());
  };

  const filteredTransactions = transactions.filter(tx => {
    if (filter === 'all') return true;
    return tx.type === filter;
  });

  if (!userData) {
    return (
      <PageContainer>
        <EmptyState>
          <EmptyIcon>🔐</EmptyIcon>
          <EmptyText>{t('transactions.loginRequired', 'Veuillez vous connecter pour voir vos transactions')}</EmptyText>
          <BuyButton to="/?noAuth">{t('auth.login', 'Se connecter')}</BuyButton>
        </EmptyState>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <BackLink to="/gcoin-store">
        ← {t('transactions.backToStore', 'Retour à la boutique')}
      </BackLink>

      <Header>
        <Title>
          <TitleIcon src={GCoinBagIcon} alt="G-Coins" />
          {t('transactions.title', 'Mes G-Coins')}
        </Title>
        <Subtitle>{t('transactions.subtitle', 'Historique de vos transactions G-Coins')}</Subtitle>

        <HeaderActions>
          <SecondaryButton to="/reversements">
            {t('transactions.payouts', 'Reversements')}
          </SecondaryButton>
        </HeaderActions>
      </Header>

      {/* Stats Cards */}
      <StatsGrid>
        <StatCard>
          <StatIconImg src={GCoinBagIcon} alt="Balance" />
          <StatValue>{formatNumber(stats.balance)}</StatValue>
          <StatLabel>{t('transactions.currentBalance', 'Solde actuel')}</StatLabel>
        </StatCard>
        <StatCard>
          <StatIconImg src={GCoinIcon} alt="Credits" style={{ filter: 'hue-rotate(90deg)' }} />
          <StatValue type="credit">+{formatNumber(stats.totalCredits)}</StatValue>
          <StatLabel>{t('transactions.totalCredits', 'Total crédités')}</StatLabel>
        </StatCard>
        <StatCard>
          <StatIconImg src={GCoinIcon} alt="Debits" style={{ filter: 'hue-rotate(-30deg) saturate(1.5)' }} />
          <StatValue type="debit">-{formatNumber(stats.totalDebits)}</StatValue>
          <StatLabel>{t('transactions.totalDebits', 'Total dépensés')}</StatLabel>
        </StatCard>
        <StatCard>
          <StatIcon>📊</StatIcon>
          <StatValue>{stats.transactionCount}</StatValue>
          <StatLabel>{t('transactions.totalTransactions', 'Transactions')}</StatLabel>
        </StatCard>
      </StatsGrid>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      {/* Transactions List */}
      <TransactionsContainer>
        <TransactionsHeader>
          <TransactionsTitle>
            📜 {t('transactions.history', 'Historique')}
          </TransactionsTitle>
          <FilterButtons>
            <FilterButton 
              active={filter === 'all'} 
              onClick={() => setFilter('all')}
            >
              {t('transactions.all', 'Tout')}
            </FilterButton>
            <FilterButton 
              active={filter === 'credit'} 
              onClick={() => setFilter('credit')}
            >
              {t('transactions.credits', 'Crédits')}
            </FilterButton>
            <FilterButton 
              active={filter === 'debit'} 
              onClick={() => setFilter('debit')}
            >
              {t('transactions.debits', 'Débits')}
            </FilterButton>
          </FilterButtons>
        </TransactionsHeader>

        {loading ? (
          <LoadingSpinner>
            <div></div>
          </LoadingSpinner>
        ) : filteredTransactions.length === 0 ? (
          <EmptyState>
            <EmptyIcon>📭</EmptyIcon>
            <EmptyText>
              {filter === 'all' 
                ? t('transactions.noTransactions', 'Aucune transaction pour le moment')
                : t('transactions.noFilteredTransactions', 'Aucune transaction de ce type')
              }
            </EmptyText>
            {filter === 'all' && (
              <BuyButton to="/gcoin-store">
                {t('transactions.buyGCoins', 'Acheter des G-Coins')}
              </BuyButton>
            )}
          </EmptyState>
        ) : (
          <TransactionsList>
            {filteredTransactions.map((tx) => (
              <TransactionItem key={tx.id}>
                <TransactionInfo>
                  <TransactionIconWrapper type={tx.type}>
                    <TransactionIconImg src={GCoinIcon} alt="G-Coin" type={tx.type} />
                  </TransactionIconWrapper>
                  <TransactionDetails>
                    <TransactionDescription>
                      {tx.description || (tx.type === 'credit' ? t('transactions.creditDescription', 'Crédit G-Coins') : t('transactions.debitDescription', 'Débit G-Coins'))}
                    </TransactionDescription>
                    <TransactionDate>{formatDate(tx.created_at)}</TransactionDate>
                  </TransactionDetails>
                </TransactionInfo>
                <TransactionAmount>
                  <Amount type={tx.type}>
                    {tx.type === 'credit' ? '+' : '-'}{formatNumber(parseFloat(tx.amount))}
                  </Amount>
                  <Balance>
                    {t('transactions.balanceAfter', 'Solde')}: {formatNumber(parseFloat(tx.balance_after))}
                  </Balance>
                </TransactionAmount>
              </TransactionItem>
            ))}
          </TransactionsList>
        )}
      </TransactionsContainer>
    </PageContainer>
  );
};

export default GCoinTransactions;

